import React, { useState } from 'react';
import { 
    Home, Users, Briefcase, Clipboard, DollarSign, 
    Package, ShoppingCart, UserPlus, LogOut, ChevronDown, ChevronRight,
    Calendar, CreditCard, Badge, Building, FileText, Clock, Route, Phone, GraduationCap, Settings, BookOpen, FilePlus
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isSidebarOpen }) => {
    const location = useLocation();
    const { logout, user } = useAuth();
    const [openMenu, setOpenMenu] = useState(null);

    const isActive = (path) => location.pathname === path;

    // HRM Sub-menu items
    const hrmItems = [
        { path: '/hrm/departments', label: 'Department', icon: Building },
        { path: '/hrm/designations', label: 'Designation', icon: Badge },
        { path: '/hrm/employees', label: 'Employee', icon: UserPlus },
        { path: '/hrm/employee-categories', label: 'Employee Categories', icon: FileText },
        { path: '/hrm/attendances', label: 'Attendance', icon: Clipboard },
        { path: '/hrm/leave-applications', label: 'Leave Applications', icon: Calendar },
        { path: '/hrm/leave-types', label: 'Leave Types', icon: Calendar },
        { path: '/hrm/leave-policies', label: 'Leave Policies', icon: FileText },
        { path: '/hrm/salary-structures', label: 'Salary', icon: CreditCard },
        { path: '/hrm/loan-applications', label: 'Loan Applications', icon: Clock },
        { path: '/hrm/movements', label: 'Movements', icon: Route },
        { path: '/hrm/holidays', label: 'Holidays', icon: Calendar },
        { path: '/hrm/settings', label: 'Settings', icon: Settings },
    ];

    // Finance Sub-menu items
    const financeItems = [
        { path: '/finance/chart-of-accounts', label: 'Chart of Accounts', icon: DollarSign },
        { path: '/finance/bank-accounts', label: 'Bank Accounts', icon: CreditCard },
        { path: '/finance/journal-entries', label: 'Journal Entries', icon: Clipboard },
        { path: '/finance/settings', label: 'Finance Settings', icon: Settings },
    ];

    // Inventory Sub-menu items
    const inventoryItems = [
        { path: '/inventory/stores', label: 'Stores', icon: Package },
        { path: '/inventory/products', label: 'Products', icon: Package },
        { path: '/inventory/stocks', label: 'Inventory Stocks', icon: Package },
    ];

    // CRM Sub-menu items
    const crmItems = [
        { path: '/crm/clients', label: 'Clients', icon: Briefcase },
        { path: '/crm/projects', label: 'Projects', icon: Briefcase },
        { path: '/crm/lifts', label: 'Lifts', icon: Briefcase },
        { path: '/crm/lift-brands', label: 'Lift Brands', icon: Briefcase },
        { path: '/crm/lift-types', label: 'Lift Types', icon: Briefcase },
        { path: '/crm/invoices', label: 'Service Invoices', icon: FileText },
        { path: '/crm/generate-invoice', label: 'Generate Invoice', icon: FilePlus },
    ];

    // Vendor Sub-menu items
    const vendorItems = [
        { path: '/vendors', label: 'Vendors', icon: ShoppingCart },
    ];

    // Reusable Menu Component
    const MenuGroup = ({ title, icon: Icon, menuKey, items }) => {
        const isOpen = openMenu === menuKey;
        
        const handleToggle = () => {
            setOpenMenu(isOpen ? null : menuKey);
        };
        
        return (
        <div className="mx-2 my-0.5">
            <button 
                onClick={handleToggle}
                className={`
                    group flex items-center justify-between w-full px-4 py-2.5 rounded-md
                    ${isOpen 
                        ? 'bg-indigo-500 text-white dark:bg-indigo-600' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900 hover:text-indigo-600 dark:hover:text-indigo-300'
                    }
                    transition-colors duration-200
                `}
                title={!isSidebarOpen ? title : ''}
            >
                <div className="flex items-center">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isOpen ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-300'}`} />
                    <span className={`
                        ml-3 whitespace-nowrap text-sm font-medium
                        ${isSidebarOpen ? 'opacity-100 block' : 'opacity-0 hidden'}
                        transition-opacity duration-300 ease-in-out
                    `}>{title}</span>
                </div>
                {isSidebarOpen && (
                    isOpen ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronRight className="w-4 h-4" />
                )}
            </button>
            
            {/* Sub-menu */}
            {isOpen && isSidebarOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-indigo-200 dark:border-indigo-700">
                    {items.map((item) => {
                        const SubIcon = item.icon;
                        return (
                            <Link 
                                key={item.path}
                                to={item.path} 
                                className={`
                                    group flex items-center pl-4 py-2 rounded-md
                                    ${isActive(item.path) 
                                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' 
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900 hover:text-indigo-600 dark:hover:text-indigo-300'
                                    }
                                    transition-colors duration-200 text-sm
                                `}
                            >
                                <SubIcon className={`w-4 h-4 flex-shrink-0 ${isActive(item.path) ? 'text-indigo-600 dark:text-indigo-300' : 'text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-300'}`} />
                                <span className="ml-3 whitespace-nowrap">{item.label}</span>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
        );
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/';
    };

    return (
        <div className={`
            ${isSidebarOpen ? 'w-64' : 'w-20'}
            bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 shadow-md transition-all duration-300 ease-in-out
            hidden md:flex flex-col
            fixed md:static inset-y-0 left-0 z-30
        `}>
            {/* Logo */}
            <div className="p-4 flex items-center justify-center border-b border-gray-200 dark:border-slate-600">
                <h1 className={`
                    text-xl font-bold whitespace-nowrap
                    ${isSidebarOpen ? 'opacity-100 block' : 'opacity-0 hidden'}
                    transition-opacity duration-300 ease-in-out
                    text-indigo-600 dark:text-indigo-400
                `}>Benfix ERP</h1>
                {!isSidebarOpen && (
                    <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">BE</div>
                )}
            </div>

            {/* User Info */}
            {isSidebarOpen && user && (
                <div className="p-4 border-b border-gray-200 dark:border-slate-600">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {user.name || user.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {user.role || 'Admin'}
                    </p>
                </div>
            )}

            {/* Navigation */}
            <nav className="mt-2 flex-1 overflow-y-auto">
                {/* Dashboard - Top Nav */}
                <Link 
                    to="/dashboard" 
                    className={`
                        group flex items-center px-4 py-2.5 mx-2 my-0.5 rounded-md
                        ${isActive('/dashboard') 
                            ? 'bg-indigo-500 text-white dark:bg-indigo-600' 
                            : 'text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900 hover:text-indigo-600 dark:hover:text-indigo-300'
                        }
                        transition-colors duration-200
                    `}
                    title={!isSidebarOpen ? 'Dashboard' : ''}
                >
                    <Home className={`w-5 h-5 flex-shrink-0 ${isActive('/dashboard') ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-300'}`} />
                    <span className={`
                        ml-3 whitespace-nowrap text-sm
                        ${isSidebarOpen ? 'opacity-100 block' : 'opacity-0 hidden'}
                        transition-opacity duration-300 ease-in-out
                    `}>Dashboard</span>
                </Link>

                

                {/* HRM Main Menu with Sub-menu */}
                <MenuGroup 
                    title="HRM" 
                    icon={Users} 
                    menuKey="hrm" 
                    items={hrmItems} 
                />

                {/* Finance Main Menu with Sub-menu */}
                <MenuGroup 
                    title="Finance" 
                    icon={DollarSign} 
                    menuKey="finance" 
                    items={financeItems} 
                />

                {/* Inventory Main Menu with Sub-menu */}
                <MenuGroup 
                    title="Inventory" 
                    icon={Package} 
                    menuKey="inventory" 
                    items={inventoryItems} 
                />

                {/* CRM Main Menu with Sub-menu */}
                <MenuGroup 
                    title="CRM" 
                    icon={Briefcase} 
                    menuKey="crm" 
                    items={crmItems} 
                />

                {/* Vendor Main Menu with Sub-menu */}
                <MenuGroup 
                    title="Vendor" 
                    icon={ShoppingCart} 
                    menuKey="vendor" 
                    items={vendorItems} 
                />

                {/* Company Settings - Standalone Menu */}
                <Link 
                    to="/settings/company" 
                    className={`
                        group flex items-center px-4 py-2.5 mx-2 my-0.5 rounded-md
                        ${isActive('/settings/company') 
                            ? 'bg-indigo-500 text-white dark:bg-indigo-600' 
                            : 'text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900 hover:text-indigo-600 dark:hover:text-indigo-300'
                        }
                        transition-colors duration-200
                    `}
                    title={!isSidebarOpen ? 'Company Settings' : ''}
                >
                    <Settings className={`w-5 h-5 flex-shrink-0 ${isActive('/settings/company') ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-300'}`} />
                    <span className={`
                        ml-3 whitespace-nowrap text-sm
                        ${isSidebarOpen ? 'opacity-100 block' : 'opacity-0 hidden'}
                        transition-opacity duration-300 ease-in-out
                    `}>Company Settings</span>
                </Link>
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-gray-200 dark:border-slate-600">
                <button 
                    onClick={handleLogout}
                    className={`
                        group flex items-center w-full px-4 py-2.5 rounded-md
                        text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900 hover:text-red-600 dark:hover:text-red-300
                        transition-colors duration-200
                    `}
                    title={!isSidebarOpen ? 'Logout' : ''}
                >
                    <LogOut className="w-5 h-5 flex-shrink-0 text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-300" />
                    <span className={`
                        ml-3 whitespace-nowrap text-sm
                        ${isSidebarOpen ? 'opacity-100 block' : 'opacity-0 hidden'}
                        transition-opacity duration-300 ease-in-out
                    `}>Logout</span>
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
